'use client';

import classNames from 'classnames';
import Fuse from 'fuse.js';
import { useId, useMemo, useState } from 'react';

const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="7" cy="7" r="5.25" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="m11 11 3 3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronDown = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="m3 4.5 3 3 3-3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FUSE_OPTIONS = {
  keys: ['searchable'],
  threshold: 0.35,
  ignoreLocation: true,
  minMatchCharLength: 2,
};

export const GuidesIndexClient = ({
  searchPlaceholder,
  filterLabel,
  filterAllLabel,
  categories,
  freeItems,
  memberItems,
  freeSection,
  membersSection,
}) => {
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const inputId = useId();
  const filterId = useId();

  const filteredFreeItems = useMemo(
    () =>
      selectedCategory
        ? freeItems.filter((item) => item.category === selectedCategory)
        : freeItems,
    [freeItems, selectedCategory],
  );

  const filteredMemberItems = useMemo(
    () =>
      selectedCategory
        ? memberItems.filter((item) => item.category === selectedCategory)
        : memberItems,
    [memberItems, selectedCategory],
  );

  const allItems = useMemo(
    () => [...filteredFreeItems, ...filteredMemberItems],
    [filteredFreeItems, filteredMemberItems],
  );

  const fuse = useMemo(() => new Fuse(allItems, FUSE_OPTIONS), [allItems]);

  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;
  const hasFilter = selectedCategory.length > 0;

  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    return fuse.search(trimmedQuery).map((result) => result.item);
  }, [fuse, isSearching, trimmedQuery]);

  const hasCategories = Array.isArray(categories) && categories.length > 0;
  const hasResults =
    filteredFreeItems.length > 0 || filteredMemberItems.length > 0;

  return (
    <div
      className={classNames('guides-index__client', {
        'guides-index__client--searching': isSearching,
      })}
    >
      <div className="guides-index__toolbar">
        <label className="guides-index__search" htmlFor={inputId}>
          <input
            id={inputId}
            type="search"
            className="guides-index__search__input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            autoComplete="off"
          />
          <span className="guides-index__search__icon" aria-hidden="true">
            <SearchIcon />
          </span>
        </label>

        {filterLabel || hasCategories ? (
          <div className="guides-index__filter">
            {filterLabel ? (
              <label className="guides-index__filter__label" htmlFor={filterId}>
                {filterLabel}
              </label>
            ) : null}

            {hasCategories ? (
              <span className="guides-index__filter__pill">
                <select
                  id={filterId}
                  className="guides-index__filter__select"
                  value={selectedCategory}
                  onChange={(event) => setSelectedCategory(event.target.value)}
                >
                  <option value="">{filterAllLabel}</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                <span
                  className="guides-index__filter__pill__chevron"
                  aria-hidden="true"
                >
                  <ChevronDown />
                </span>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {isSearching ? (
        <div className="guides-index__results">
          {searchResults.length > 0 ? (
            <div className="guides-index__grid">
              {searchResults.map((item) => (
                <div key={item.id} className="guides-index__cell">
                  {item.node}
                </div>
              ))}
            </div>
          ) : (
            <p className="guides-index__empty">
              Aucune ressource ne correspond à « {trimmedQuery} »
              {hasFilter ? ` dans la catégorie « ${selectedCategory} »` : ''}.
            </p>
          )}
        </div>
      ) : (
        <>
          {filteredMemberItems.length > 0 ? (
            <section className="guides-index__section">
              {membersSection}

              <div className="guides-index__grid">
                {filteredMemberItems.map((item) => (
                  <div key={item.id} className="guides-index__cell">
                    {item.node}
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {filteredFreeItems.length > 0 ? (
            <section className="guides-index__section">
              {freeSection}

              <div className="guides-index__grid">
                {filteredFreeItems.map((item) => (
                  <div key={item.id} className="guides-index__cell">
                    {item.node}
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {!hasResults && hasFilter ? (
            <p className="guides-index__empty">
              Aucune ressource dans la catégorie « {selectedCategory} ».
            </p>
          ) : null}
        </>
      )}
    </div>
  );
};
